import type { QueryKey } from "@tanstack/react-query";

/** Query-key factory for prequalification submissions (leadKeys-style).
 * Every read registers under the `prequalKeys.all` prefix so a mutation can
 * cover list + stats + detail + activity with ONE prefix invalidation.
 *
 * <b>The filter object goes into the key WHOLE.</b> Each list hook spreads its
 * entire query in, so every parameter — including `q` and `sort` — is part of the
 * cache identity for free, and a new one added to the API type is covered the
 * moment it exists. Never narrow these to a hand-picked field list: with the
 * client's 5-minute `staleTime` and no refetch-on-focus, a parameter missing from
 * the key means typing in the search box or flipping the sort re-renders the
 * PREVIOUS page of results and never refetches. */
export const prequalKeys = {
  all: ["prequalification"] as const,
  list: (filters: Record<string, unknown>): QueryKey => [...prequalKeys.all, "list", filters],
  stats: (filters: Record<string, unknown>): QueryKey => [...prequalKeys.all, "stats", filters],
  detail: (id: string) => [...prequalKeys.all, "detail", id] as const,
  /** The explained answer rows behind one submission. Separate from `detail`
   *  because it is a separate read — the drill-in drawer fetches it lazily when
   *  it opens rather than every row paying for it on render. Still nested under
   *  `all`, so anything that invalidates a submission covers it. */
  answers: (id: string) => [...prequalKeys.all, "answers", id] as const,
  activity: (id: string) => [...prequalKeys.all, "activity", id] as const,
  /** The send dialog's state and the detail page's link-back. Nested under
   *  `all`, so any submission mutation refreshes it. */
  buyerCrm: (id: string) => [...prequalKeys.all, "buyer-crm", id] as const,
  /** What a resend would send, for the send-preview modal. Nested under `all`
   *  so a regenerate — which changes the documents the draft lists — evicts it
   *  too; a stale draft would name the attachments of the PREVIOUS set. */
  resendDraft: (id: string) => [...prequalKeys.all, "resend-draft", id] as const,
  /** Tracked invites. Nested under `all` so an invite mutation and a
   *  submission mutation share one prefix invalidation — completing an
   *  invite also mints a submission, so the two lists move together. */
  invites: (filters: Record<string, unknown>): QueryKey => [
    ...prequalKeys.all,
    "invites",
    filters,
  ],
  /** In-progress drafts. Nested under `all` for the same reason as invites —
   *  finalizing a draft mints a submission and completes its invite, so the
   *  three lists move together and one prefix invalidation has to cover them. */
  drafts: (filters: Record<string, unknown>): QueryKey => [
    ...prequalKeys.all,
    "drafts",
    filters,
  ],
};

/** Query-key factory for the Document Language Editor. A dedicated sub-prefix
 * so a copy mutation invalidates the editor reads WITHOUT nuking the submission
 * list/stats/detail caches. */
export const prequalDocumentKeys = {
  all: ["prequalification", "documents"] as const,
  list: (partnerBrandId?: string | null): QueryKey => [
    ...prequalDocumentKeys.all,
    "list",
    partnerBrandId ?? null,
  ],
  sets: (): QueryKey => [...prequalDocumentKeys.all, "sets"],
};

/** Query-key factory for the Program Brochure admin. Its own sub-prefix beside
 * `prequalDocumentKeys` for the same reason: upload / restore / withdraw are
 * frequent while a brochure is being set up, and none of them should evict the
 * submission list or the copy editor.
 *
 * ONE read, so ONE key — the overview endpoint answers for both roles in a
 * single request, and every mutation returns the changed role's state, so the
 * three mutations all invalidate `all`.
 *
 * <b>There is deliberately no key for the download link.</b> That endpoint mints
 * a time-limited SAS; with the client's 5-minute `staleTime` a cached one would
 * be handed out after it had expired. It is fetched imperatively at the moment
 * View is clicked and never stored. */
export const programBrochureKeys = {
  all: ["prequalification", "program-brochures"] as const,
  overview: (): QueryKey => [...programBrochureKeys.all, "overview"],
};
